import { useState } from 'react';
import { Icon } from '@iconify/react';
import { MessageSquare, Brain, Zap, Shield, Settings, Play, Code, Database } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function ChatGPTCustomActionsLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Create Custom GPT
Go to ChatGPT and create a new Custom GPT

# 2. Add Memara actions
Import Memara's OpenAPI schema for memory actions

# 3. Configure authentication
Set up API key authentication for Memara

# 4. Start using memory actions
Your Custom GPT can now create and search memories`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };

  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="custom_gpt_action"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 shadow" aria-label="ChatGPT Logo">
          <Icon icon="simple-icons:openai" width={48} height={48} />
        </span>
      }
      title="ChatGPT Custom Actions"
      description="Enterprise-grade persistent memory for ChatGPT Custom GPTs. Transform conversational AI into intelligent assistants with long-term context retention, semantic memory operations, and multi-tenant knowledge management through OpenAPI integration."
      features={[
        { icon: <MessageSquare className="h-4 w-4 text-green-600" />, title: 'Persistent Conversation Memory', description: 'ChatGPT maintains context across all conversations with intelligent memory persistence and retrieval.' },
        { icon: <Brain className="h-4 w-4 text-green-600" />, title: 'Semantic Memory Operations', description: 'Built-in memory actions for creating, searching, and managing contextual knowledge with vector similarity.' },
        { icon: <Code className="h-4 w-4 text-green-600" />, title: 'OpenAPI Schema Integration', description: 'Standards-compliant API integration with comprehensive OpenAPI documentation and authentication.' },
        { icon: <Database className="h-4 w-4 text-green-600" />, title: 'Multi-Tenant Knowledge Base', description: 'Isolated memory spaces with enterprise-grade security and role-based access controls.' },
        { icon: <Zap className="h-4 w-4 text-green-600" />, title: 'Real-time Memory Access', description: 'Sub-200ms memory operations with instant context retrieval for seamless conversational flow.' },
        { icon: <Shield className="h-4 w-4 text-green-600" />, title: 'API Key Security', description: 'API key authentication with isolated memory spaces.' },
      ]}
      setupSteps={[
        'Create Custom GPT in ChatGPT interface',
        'Import Memara OpenAPI schema for memory actions',
        'Configure secure API key authentication',
        'Deploy Custom GPT with persistent memory capabilities',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-green-600 hover:bg-green-700 text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-green-200 text-green-600 hover:bg-green-50 px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Settings className="w-4 h-4 mr-2" />,
        },
      ]}
      screenshots={[
        {
          id: 'chatgpt-setup',
          title: 'Custom GPT Setup',
          description: 'Create your Custom GPT and configure Memara memory actions in the ChatGPT interface',
          storyName: 'integration-real-screenshots-ui-components-with-dummy-data--chat-gpt-setup-flow',
          placeholder: 'ChatGPT Custom GPT creation interface with Memara actions configuration',
          badge: 'Step 1'
        },
        {
          id: 'memory-creation',
          title: 'Memory Creation Interface', 
          description: 'Create and store important information with the intuitive memory creation interface',
          storyName: 'integration-real-screenshots-ui-components-with-dummy-data--memory-creation-interface',
          placeholder: 'Memory creation form with rich text editing and tagging capabilities',
          badge: 'Step 2'
        },
        {
          id: 'memory-search',
          title: 'Intelligent Memory Search',
          description: 'Find any information instantly with AI-powered semantic search across all your memories',
          storyName: 'integration-real-screenshots-ui-components-with-dummy-data--memory-search-results',
          placeholder: 'Search interface showing relevant memory results with context highlighting',
          badge: 'Live Demo'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
    />
  );
} 